import { logger } from "@/utils/logger";
/**
 * 认证检查工具 - 确保用户状态正确恢复
 * 与原系统完全兼容的认证逻辑
 */
import { TokenManager } from "./auth";
import { useUserStore } from "@/stores/user";

// Share the actual identity request across bootstrap and concurrent route guards.
// Keep its settled result so later navigation does not trigger another request.
let identityRestoration: Promise<boolean> | null = null;

/**
 * 检查并恢复用户认证状态
 * 优化：添加超时保护和更健壮的错误处理
 */
export async function checkAndRestoreAuth(
  options: { waitForIdentity?: boolean } = {},
): Promise<boolean> {
  try {
    const userStore = useUserStore();

    // 如果已经认证，直接返回
    if (userStore.isAuthenticated && userStore.currentUser.username) {
      logger.log("用户已认证:", userStore.currentUser.username);
      if (options.waitForIdentity) {
        if (userStore.currentUser.roles.length > 0) return true;
        return identityRestoration ? identityRestoration : false;
      }
      return true;
    }

    // 尝试从Cookie恢复
    const username = TokenManager.getUsername();
    const token = TokenManager.getToken();

    if (username && token) {
      logger.log("从Cookie恢复用户状态:", username);

      // 🆕 优先策略：如果有有效的token和username，先认为用户已认证
      // 允许页面先加载，用户信息在后台异步完成
      // 这样可以避免因API慢导致的登录跳转
      if (!userStore.isAuthenticated) {
        // 先设置基础认证状态和用户信息，允许页面访问
        userStore.setAuthenticated(true);
        userStore.setToken(token);

        // 尝试从Cookie加载权限（避免空权限导致访问被拒）
        const savedPermissions = TokenManager.getPermissions();

        // 设置基础用户对象（避免 currentUser.username 为空）
        userStore.setUser({
          id: username,
          username: username,
          email: username,
          roles: [], // 后续从API加载
          permissions: savedPermissions || [], // 从Cookie加载或使用空数组
          lastLoginTime: new Date().toISOString(),
        });

        logger.log(
          `✅ 已设置基础认证状态（完整用户信息将在后台加载，权限数: ${savedPermissions?.length || 0}）`,
        );
      }

      // Ordinary pages retain fast bootstrap; role-protected pages await the same
      // request before evaluating access. A failed or timed-out identity fails closed.
      let timeoutId: ReturnType<typeof setTimeout>;
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("认证恢复超时")), 10000);
      });
      identityRestoration = Promise.race([
        userStore.setCurrentUser({ username, token, expires: 30 }),
        timeout,
      ])
        .then((result) => {
          if (!result.success) logger.warn("用户完整信息加载失败");
          return result.success;
        })
        .catch(() => {
          logger.warn("用户完整信息加载失败或超时");
          return false;
        })
        .finally(() => clearTimeout(timeoutId));

      return options.waitForIdentity ? identityRestoration : true;
    }

    logger.log("未找到有效的认证信息");
    return false;
  } catch (error) {
    logger.error("认证检查失败:", error);
    return false;
  }
}

/**
 * 清除认证状态
 */
export function clearAuth(): void {
  try {
    const userStore = useUserStore();

    // 清除Cookie
    TokenManager.clearCredentials();
    identityRestoration = null;

    // 清除用户状态
    userStore.logout();

    logger.log("认证状态已清除");
  } catch (error) {
    logger.error("清除认证状态失败:", error);
  }
}

/**
 * 检查是否需要登录
 */
export function needsLogin(): boolean {
  const username = TokenManager.getUsername();
  const token = TokenManager.getToken();

  return !username || !token;
}

/**
 * 获取认证状态信息
 */
export function getAuthInfo() {
  const username = TokenManager.getUsername();
  const token = TokenManager.getToken();
  const isAuthenticated = TokenManager.isAuthenticated();

  return {
    username,
    token: token ? "***" : null, // 隐藏实际token
    isAuthenticated,
    hasCredentials: !!(username && token),
  };
}

export default {
  checkAndRestoreAuth,
  clearAuth,
  needsLogin,
  getAuthInfo,
};
